import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, ModalBody, ModalHeader, Spinner } from "reactstrap";
import moment from "moment";
import MonthlyPhoneNotesModal from "./MonthlyPhoneNotesModal";
import { useMpcLargeModalSize } from "./mpcLargeModal";
import { MPC_COPY } from "./monthlyPhoneConsultationLabels";

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const formatNoteDate = (value) => {
  if (!value) return "—";
  return moment(value).format("MMM D, YYYY h:mm A");
};

const formatRoleLabel = (role) => {
  if (role === "careprovider") return "Provider";
  if (role === "staff") return "Staff";
  return "Member";
};

const capitalizeWord = (value) => {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

const formatPatientName = (value) => {
  const raw = String(value || "").trim();
  if (!raw) return "-";

  if (raw.includes(",")) {
    const [lastName, firstName] = raw.split(",").map((part) => part.trim());
    return [firstName, lastName]
      .filter(Boolean)
      .map((part) => part.split(/\s+/).map(capitalizeWord).join(" "))
      .join(" ");
  }

  return raw
    .split(/\s+/)
    .map(capitalizeWord)
    .join(" ");
};

const getProfileImageSrc = (profileImage) => {
  if (!profileImage) return "";
  if (String(profileImage).startsWith("http")) return profileImage;
  return `${process.env.REACT_APP_VIDEO_CALL_REQUEST}/${profileImage}`;
};

const PatientRowCell = ({ patientName, profileImage, email, phone, isActive, onViewPatient, row }) => {
  const [imageError, setImageError] = useState(false);
  const imageSrc = getProfileImageSrc(profileImage);
  const showImage = Boolean(imageSrc) && !imageError;
  const contactParts = [email, phone].filter(Boolean);

  return (
    <div className="mpc-team-detail-patient-cell">
      <div className="mpr-patient-profile-avatar-wrap">
        {showImage ? (
          <img
            src={imageSrc}
            alt={patientName}
            className="mpr-patient-profile-avatar"
            onError={() => setImageError(true)}
          />
        ) : (
          <span className="mpr-patient-profile-fallback-icon">
            <i className="mdi mdi-account" />
          </span>
        )}
      </div>
      <div className="mpc-team-detail-patient-copy">
        <div className="mpc-team-detail-patient-top">
          {onViewPatient ? (
            <button type="button" className="mpc-patient-link" onClick={() => onViewPatient(row)}>
              {patientName}
            </button>
          ) : (
            <span className="mpc-team-detail-patient-name">{patientName}</span>
          )}
          <span className={`mpr-patient-active-tag ${isActive ? "active" : "inactive"}`}>
            {isActive ? "Active" : "Inactive"}
          </span>
        </div>
        {contactParts.length > 0 ? (
          <div className="mpc-team-detail-patient-contact">{contactParts.join(" · ")}</div>
        ) : (
          <div className="mpc-team-detail-patient-contact muted">No contact info</div>
        )}
      </div>
    </div>
  );
};

const SortButton = ({ label, field, sortField, sortOrder, onSort }) => {
  const isActive = sortField === field;
  const icon = !isActive ? "mdi-unfold-more-horizontal" : sortOrder === "asc" ? "mdi-arrow-up" : "mdi-arrow-down";

  return (
    <button type="button" className={`mpc-team-detail-sort-btn ${isActive ? "active" : ""}`} onClick={() => onSort(field)}>
      <span>{label}</span>
      <i className={`mdi ${icon}`} aria-hidden="true" />
    </button>
  );
};

const normalizePatients = (patients = []) =>
  patients.map((patient) => {
    const consultations = Array.isArray(patient.consultations) ? patient.consultations : [];
    const latestConsultation = consultations[0];

    return {
      ...patient,
      _id: patient._id || `${patient.email}-${patient.phone}`,
      patientName: formatPatientName(patient.patientName),
      email: patient.email || "",
      phone: patient.phone || "",
      consultationCount: Number(patient.consultationCount) || consultations.length,
      consultations,
      monthlyPhoneNotes: consultations,
      monthlyPhoneNotesCount: Number(patient.consultationCount) || consultations.length,
      latestConsultationAt: latestConsultation?.createdAt || latestConsultation?.messageDate || null,
      isActive: Boolean(patient.isActive)
    };
  });

const TeamMemberConsultationsModal = ({
  isOpen,
  toggle,
  detail,
  loading = false,
  onViewPatient,
  mpcCopy,
  programType = "rpm"
}) => {
  const [searchText, setSearchText] = useState("");
  const [notesPatient, setNotesPatient] = useState(null);
  const [sortField, setSortField] = useState("patientName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const modalRef = useRef(null);

  const copy = mpcCopy || MPC_COPY;
  const consultationTitle = copy.consultationTitle;
  const teamMember = detail?.teamMember || {};
  const patients = detail?.patients || [];
  const monthLabel = detail?.monthLabel || "Selected month";
  const totalConsultations = detail?.totalConsultations ?? 0;
  const roleLabel = formatRoleLabel(teamMember.role);

  useEffect(() => {
    if (isOpen) {
      setSearchText("");
      setNotesPatient(null);
      setSortField("patientName");
      setSortOrder("asc");
      setPage(1);
      setPageSize(10);
    }
  }, [isOpen, teamMember.userId, monthLabel]);

  useMpcLargeModalSize(isOpen, modalRef);

  useEffect(() => {
    setPage(1);
  }, [searchText, sortField, sortOrder, pageSize]);

  const rows = useMemo(() => normalizePatients(patients), [patients]);

  const filteredRows = useMemo(() => {
    const query = searchText.toLowerCase().trim();
    if (!query) {
      return rows;
    }

    return rows.filter((row) =>
      [row.patientName, row.email, row.phone]
        .map((value) => String(value || "").toLowerCase())
        .some((value) => value.includes(query))
    );
  }, [rows, searchText]);

  const sortedRows = useMemo(() => {
    const direction = sortOrder === "asc" ? 1 : -1;

    return [...filteredRows].sort((a, b) => {
      if (sortField === "latestConsultationAt") {
        const aTime = a.latestConsultationAt ? new Date(a.latestConsultationAt).getTime() : 0;
        const bTime = b.latestConsultationAt ? new Date(b.latestConsultationAt).getTime() : 0;
        return (aTime - bTime) * direction;
      }

      if (sortField === "consultationCount") {
        return ((a.consultationCount || 0) - (b.consultationCount || 0)) * direction;
      }

      return String(a[sortField] || "").localeCompare(String(b[sortField] || ""), undefined, {
        sensitivity: "base",
        numeric: true
      }) * direction;
    });
  }, [filteredRows, sortField, sortOrder]);

  const totalPages = Math.max(1, Math.ceil(sortedRows.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageStart = (currentPage - 1) * pageSize;
  const pageRows = sortedRows.slice(pageStart, pageStart + pageSize);
  const showingFrom = sortedRows.length === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(pageStart + pageSize, sortedRows.length);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortField(field);
    setSortOrder(field === "patientName" ? "asc" : "desc");
  };

  const handleClose = () => {
    setNotesPatient(null);
    setSearchText("");
    toggle();
  };

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, start + maxButtons - 1);
    const adjustedStart = Math.max(1, end - maxButtons + 1);
    return Array.from({ length: end - adjustedStart + 1 }, (_, index) => adjustedStart + index);
  }, [currentPage, totalPages]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        centered
        innerRef={modalRef}
        className="mpc-large-modal mpc-team-detail-modal"
        dialogClassName="mpc-large-modal-dialog mpc-team-detail-dialog"
        contentClassName="mpc-large-modal-content mpc-team-detail-content"
      >
        <ModalHeader toggle={handleClose} className="mpc-team-detail-header">
          <div className="mpc-team-detail-header-copy">
            <div className="mpc-team-detail-header-title">
              <span className="mpc-team-detail-eyebrow">{consultationTitle}</span>
              <h5 className="mpc-team-detail-name">{teamMember.name || "Team member"}</h5>
            </div>
            <div className="mpc-team-detail-header-meta">
              <span className={`mpr-team-role-tag ${roleLabel === "Provider" ? "provider" : "staff"}`}>
                {roleLabel}
              </span>
              <span className="mpc-team-detail-meta-chip">{monthLabel}</span>
              <span className="mpc-team-detail-meta-chip">
                {totalConsultations} consultation{totalConsultations === 1 ? "" : "s"}
              </span>
              {!loading && patients.length > 0 ? (
                <span className="mpc-team-detail-meta-chip">
                  {patients.length} patient{patients.length === 1 ? "" : "s"}
                </span>
              ) : null}
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="mpc-team-detail-body">
          {loading ? (
            <div className="mpc-team-detail-loading">
              <Spinner color="primary" />
              <span>Loading patient consultations…</span>
            </div>
          ) : patients.length === 0 ? (
            <div className="mpc-notes-empty">{copy.teamMemberDetailEmptyMessage}</div>
          ) : (
            <>
              <div className="mpc-team-detail-toolbar">
                <label className="mpc-team-detail-search" htmlFor="mpc-team-detail-search-input">
                  <i className="mdi mdi-magnify" aria-hidden="true" />
                  <input
                    id="mpc-team-detail-search-input"
                    type="text"
                    placeholder="Search patient name, email, or phone..."
                    value={searchText}
                    onChange={(event) => setSearchText(event.target.value)}
                  />
                  {searchText ? (
                    <button
                      type="button"
                      className="mpc-team-detail-search-clear"
                      onClick={() => setSearchText("")}
                      aria-label="Clear search"
                    >
                      <i className="mdi mdi-close-circle" />
                    </button>
                  ) : null}
                </label>
                <span className="mpc-team-detail-results-pill">
                  {filteredRows.length} of {rows.length} patients
                </span>
              </div>

              <div className="mpc-team-detail-table-shell">
                <table className="mpc-team-detail-table">
                  <colgroup>
                    <col className="mpc-team-detail-col-patient" />
                    <col className="mpc-team-detail-col-consultations" />
                    <col className="mpc-team-detail-col-date" />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="mpc-team-detail-col-patient" scope="col">
                        <SortButton
                          label="Patient"
                          field="patientName"
                          sortField={sortField}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="mpc-team-detail-col-consultations is-center" scope="col">
                        <SortButton
                          label={copy.consultationsColumnLabel}
                          field="consultationCount"
                          sortField={sortField}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                      <th className="mpc-team-detail-col-date" scope="col">
                        <SortButton
                          label="Latest consultation"
                          field="latestConsultationAt"
                          sortField={sortField}
                          sortOrder={sortOrder}
                          onSort={handleSort}
                        />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.length === 0 ? (
                      <tr>
                        <td colSpan={3}>
                          <div className="mpc-empty-state mpc-team-detail-empty">
                            <i className="mdi mdi-account-search-outline" />
                            <p>No patients match your search.</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      pageRows.map((row) => (
                        <tr key={row._id}>
                          <td className="mpc-team-detail-col-patient">
                            <PatientRowCell
                              patientName={row.patientName}
                              profileImage={row.profileImage}
                              email={row.email}
                              phone={row.phone}
                              isActive={row.isActive}
                              onViewPatient={onViewPatient}
                              row={row}
                            />
                          </td>
                          <td
                            className="mpc-team-detail-col-consultations is-center"
                            data-label={copy.consultationsColumnLabel}
                          >
                            {row.consultationCount > 0 ? (
                              <button
                                type="button"
                                className="mpc-notes-count-btn"
                                onClick={() => setNotesPatient(row)}
                                title="View consultation details"
                              >
                                {row.consultationCount}
                                <i className="mdi mdi-chevron-right" />
                              </button>
                            ) : (
                              <span className="mpc-muted-dash">—</span>
                            )}
                          </td>
                          <td className="mpc-team-detail-col-date" data-label="Latest consultation">
                            <span className="mpc-team-detail-date-cell">{formatNoteDate(row.latestConsultationAt)}</span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="mpc-team-detail-footer">
                <div className="mpc-team-detail-footer-left">
                  <label className="mpc-team-detail-page-size" htmlFor="mpc-team-detail-page-size">
                    Rows
                    <select
                      id="mpc-team-detail-page-size"
                      value={pageSize}
                      onChange={(event) => setPageSize(Number(event.target.value))}
                    >
                      {PAGE_SIZE_OPTIONS.map((size) => (
                        <option key={size} value={size}>
                          {size}
                        </option>
                      ))}
                    </select>
                  </label>
                  <span className="mpc-team-detail-footer-total">
                    Showing {showingFrom}-{showingTo} of {sortedRows.length}
                  </span>
                </div>

                {totalPages > 1 ? (
                  <div className="mpc-team-detail-pagination" role="navigation" aria-label="Patient list pagination">
                    <button
                      type="button"
                      className="mpc-team-detail-page-btn mpc-team-detail-page-btn--edge"
                      disabled={currentPage === 1}
                      onClick={() => setPage(1)}
                    >
                      First
                    </button>
                    <button
                      type="button"
                      className="mpc-team-detail-page-btn mpc-team-detail-page-btn--nav"
                      disabled={currentPage === 1}
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                    >
                      Back
                    </button>
                    {pageNumbers.map((pageNumber) => (
                      <button
                        key={pageNumber}
                        type="button"
                        className={`mpc-team-detail-page-btn ${pageNumber === currentPage ? "active" : ""}`}
                        onClick={() => setPage(pageNumber)}
                      >
                        {pageNumber}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="mpc-team-detail-page-btn mpc-team-detail-page-btn--nav"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                    >
                      Next
                    </button>
                    <button
                      type="button"
                      className="mpc-team-detail-page-btn mpc-team-detail-page-btn--edge"
                      disabled={currentPage === totalPages}
                      onClick={() => setPage(totalPages)}
                    >
                      Last
                    </button>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <MonthlyPhoneNotesModal
        isOpen={Boolean(notesPatient)}
        toggle={() => setNotesPatient(null)}
        patient={notesPatient}
        notes={notesPatient?.consultations}
        titlePrefix={consultationTitle}
        emptyMessage={copy.consultationEmptyMessage}
        tone="success"
      />
    </>
  );
};

export default TeamMemberConsultationsModal;
